/**
 * ManageOrganizations — moderator tool for managing organizations during a live sim.
 * Self-contained DashboardSection card matching Facilitator Dashboard style.
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import type { Organization, OrgMembership } from '@/lib/queries'

const API = import.meta.env.VITE_API_URL ?? ''

interface Props {
  simId: string
  roles: { id: string; character_name: string; country_code: string }[]
}

const ROLE_OPTIONS = [
  'member', 'founding_member', 'permanent_member', 'rotating_member',
  'co_leader', 'leader', 'observer', 'dialogue_partner', 'internal',
]

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

async function apiCall(method: string, path: string, body?: unknown) {
  const opts: RequestInit = { method, headers: { 'Content-Type': 'application/json' } }
  if (body) opts.body = JSON.stringify(body)
  const res = await fetch(`${API}${path}`, opts)
  return res.json()
}

export function ManageOrganizations({ simId, roles }: Props) {
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [memberships, setMemberships] = useState<OrgMembership[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)

  // Card collapse/resize — matching DashboardSection pattern
  const storageKey = 'fac_section_Manage_Organizations'
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem(`${storageKey}_c`) === '1')
  const [height, setHeight] = useState<number | null>(() => {
    const stored = sessionStorage.getItem(`${storageKey}_h`)
    return stored ? Number(stored) : null
  })
  const resizing = useRef(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const toggleCollapse = () => {
    const next = !collapsed
    setCollapsed(next)
    sessionStorage.setItem(`${storageKey}_c`, next ? '1' : '0')
  }

  const onResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    resizing.current = true
    const startY = e.clientY
    const startH = contentRef.current?.offsetHeight || 200
    const onMove = (ev: MouseEvent) => {
      if (!resizing.current) return
      const newH = Math.max(60, startH + (ev.clientY - startY))
      setHeight(newH)
      sessionStorage.setItem(`${storageKey}_h`, String(newH))
    }
    const onUp = () => { resizing.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const countries = [...new Set(roles.map(r => r.country_code))].sort()

  const load = useCallback(async () => {
    try {
      const res = await apiCall('GET', `/api/sim/${simId}/organizations`)
      if (res.success) {
        setOrgs(res.data.organizations)
        setMemberships(res.data.memberships)
      }
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [simId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const ch = supabase.channel(`org-manage-${simId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'organizations', filter: `sim_run_id=eq.${simId}` }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'org_memberships', filter: `sim_run_id=eq.${simId}` }, () => load())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId, load])

  function flash(msg: string) {
    setFeedback(msg)
    setTimeout(() => setFeedback(null), 3000)
  }

  function getMembersForOrg(orgId: string) {
    return memberships.filter(m => m.org_id === orgId)
  }

  function getRolesForCountry(cc: string) {
    return roles.filter(r => r.country_code === cc)
  }

  function getRoleName(roleId: string | null) {
    if (!roleId) return ''
    const r = roles.find(rl => rl.id === roleId)
    return r ? r.character_name : roleId
  }

  async function handleUpdateOrg(orgId: string, updates: Record<string, unknown>) {
    const res = await apiCall('PUT', `/api/sim/${simId}/organizations/${orgId}`, updates)
    if (res.success) { flash('Updated'); load() }
    else flash(`Error: ${res.error}`)
  }

  async function handleDeleteOrg(orgId: string, name: string) {
    if (!confirm(`Dissolve "${name}"? This removes all members.`)) return
    const res = await apiCall('DELETE', `/api/sim/${simId}/organizations/${orgId}`)
    if (res.success) { flash(`${name} dissolved`); load() }
    else flash(`Error: ${res.error}`)
  }

  async function handleAddMember(orgId: string, countryCode: string) {
    const countryRoles = getRolesForCountry(countryCode)
    const roleId = countryRoles[0]?.id || ''
    const res = await apiCall('POST', `/api/sim/${simId}/organizations/${orgId}/members`, {
      country_code: countryCode, role_id: roleId, role_in_org: 'member',
    })
    if (res.success) { flash(`${cap(countryCode)} added`); load() }
    else flash(`Error: ${res.error}`)
  }

  async function handleRemoveMember(orgId: string, countryCode: string) {
    if (!confirm(`Remove ${cap(countryCode)}?`)) return
    const res = await apiCall('DELETE', `/api/sim/${simId}/organizations/${orgId}/members/${countryCode}`)
    if (res.success) { flash('Removed'); load() }
    else flash(`Error: ${res.error}`)
  }

  async function handleUpdateMember(orgId: string, cc: string, updates: Record<string, unknown>) {
    const res = await apiCall('PUT', `/api/sim/${simId}/organizations/${orgId}/members/${cc}`, updates)
    if (res.success) load()
    else flash(`Error: ${res.error}`)
  }

  async function handleCreateOrg(name: string, description: string) {
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    const res = await apiCall('POST', `/api/sim/${simId}/organizations`, {
      id, sim_name: name, description, members: [],
    })
    if (res.success) { flash(`Created ${name}`); setShowCreate(false); load() }
    else flash(`Error: ${res.error}`)
  }

  /* ── Render — DashboardSection card wrapper ──────────────── */

  return (
    <section className={`bg-card border border-border rounded-lg ${collapsed ? 'p-0' : 'p-5 pb-0'}`}>
      {/* Card header — title + badge + collapse toggle */}
      <div
        className={`flex items-center gap-2 ${collapsed ? 'px-5 py-2.5' : 'mb-3'} cursor-pointer select-none`}
        onClick={toggleCollapse}
      >
        <h3 className="font-heading text-h3 text-text-primary flex-1">Manage Organizations</h3>
        <span className="font-data text-caption bg-action/10 text-action px-2 py-0.5 rounded-full">
          {orgs.length} orgs
        </span>
        <span className="text-text-secondary/40 text-caption font-mono">{collapsed ? '+' : '−'}</span>
      </div>

      {!collapsed && (
        <>
          <div ref={contentRef} className="overflow-y-auto" style={height ? { height, maxHeight: height } : {}}>
            {loading ? (
              <p className="font-body text-body-sm text-text-secondary py-2">Loading...</p>
            ) : (
              <div className="space-y-3">
                {feedback && (
                  <div className="font-body text-caption bg-action/15 text-action px-3 py-1 rounded">{feedback}</div>
                )}

                {/* New org button */}
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowCreate(!showCreate)}
                    className="font-body text-caption font-medium bg-action/10 text-action px-3 py-1.5 rounded hover:bg-action/20 transition-colors"
                  >
                    + New Organization
                  </button>
                </div>

                {showCreate && <CreateOrgInline onSubmit={handleCreateOrg} onCancel={() => setShowCreate(false)} />}

                {/* Organization list */}
                {orgs.map(org => {
                  const members = getMembersForOrg(org.id)
                  const expanded = expandedOrg === org.id
                  const memberCountries = new Set(members.map(m => m.country_code))
                  const availableCountries = countries.filter(c => !memberCountries.has(c))
                  const chairName = getRoleName(org.chair_role_id)

                  return (
                    <div key={org.id} className="bg-base rounded-lg border border-border p-3">
                      {/* Org header row */}
                      <div
                        className="flex items-center gap-3 cursor-pointer select-none"
                        onClick={() => setExpandedOrg(expanded ? null : org.id)}
                      >
                        <h4 className="font-heading text-caption text-text-primary font-medium uppercase tracking-wider flex-1">
                          {org.sim_name}
                        </h4>
                        <span className="font-data text-caption text-text-secondary">{members.length} members</span>
                        {chairName && (
                          <span className="font-body text-caption text-text-secondary">
                            Chair: <span className="text-text-primary">{chairName}</span>
                          </span>
                        )}
                        <span className="text-text-secondary/40 text-caption font-mono">{expanded ? '−' : '+'}</span>
                      </div>

                      {/* Expanded detail */}
                      {expanded && (
                        <div className="mt-3 space-y-2">
                          {org.description && (
                            <p className="font-body text-caption text-text-secondary">{org.description}</p>
                          )}

                          {/* Chair selector + dissolve */}
                          <div className="flex items-center gap-3">
                            <span className="font-body text-caption text-text-secondary w-12">Chair</span>
                            <select
                              className="bg-card border border-border rounded px-2 py-1 font-body text-caption text-text-primary"
                              value={org.chair_role_id || ''}
                              onChange={e => handleUpdateOrg(org.id, { chair_role_id: e.target.value })}
                            >
                              <option value="">— none —</option>
                              {members.map(m => {
                                const r = roles.find(rl => rl.id === m.role_id)
                                return (
                                  <option key={`${m.country_code}-${m.role_id}`} value={m.role_id || m.country_code}>
                                    {r ? `${r.character_name} (${cap(m.country_code)})` : cap(m.country_code)}
                                  </option>
                                )
                              })}
                            </select>
                            <div className="flex-1" />
                            <button
                              onClick={() => handleDeleteOrg(org.id, org.sim_name)}
                              className="font-body text-caption text-danger/60 hover:text-danger transition-colors"
                            >
                              Dissolve
                            </button>
                          </div>

                          {/* Members */}
                          <div className="space-y-1">
                            {members.map(m => (
                              <div key={m.country_code} className="flex items-center gap-3 py-0.5">
                                <span className="font-body text-caption text-text-primary w-24 shrink-0">
                                  {cap(m.country_code)}
                                </span>
                                <select
                                  className="bg-card border border-border rounded px-1 py-0.5 font-body text-caption text-text-primary min-w-0"
                                  value={m.role_id || ''}
                                  onChange={e => handleUpdateMember(org.id, m.country_code, { role_id: e.target.value })}
                                >
                                  <option value="">— rep —</option>
                                  {getRolesForCountry(m.country_code).map(r => (
                                    <option key={r.id} value={r.id}>{r.character_name}</option>
                                  ))}
                                </select>
                                <select
                                  className="bg-card border border-border rounded px-1 py-0.5 font-body text-caption text-text-primary"
                                  value={m.role_in_org}
                                  onChange={e => handleUpdateMember(org.id, m.country_code, { role_in_org: e.target.value })}
                                >
                                  {ROLE_OPTIONS.map(r => (
                                    <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                                  ))}
                                </select>
                                <label className="flex items-center gap-1 font-data text-[10px] text-text-secondary shrink-0">
                                  <input
                                    type="checkbox"
                                    checked={m.has_veto}
                                    onChange={e => handleUpdateMember(org.id, m.country_code, { has_veto: e.target.checked })}
                                    className="w-3 h-3"
                                  />
                                  veto
                                </label>
                                <button
                                  onClick={() => handleRemoveMember(org.id, m.country_code)}
                                  className="font-body text-caption text-danger/40 hover:text-danger transition-colors shrink-0"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}

                            {/* Add member */}
                            {availableCountries.length > 0 && (
                              <div className="flex items-center gap-2 pt-1">
                                <select
                                  id={`add-member-${org.id}`}
                                  className="bg-card border border-border rounded px-2 py-0.5 font-body text-caption text-text-primary"
                                  defaultValue=""
                                >
                                  <option value="" disabled>Add country...</option>
                                  {availableCountries.map(c => <option key={c} value={c}>{cap(c)}</option>)}
                                </select>
                                <button
                                  onClick={() => {
                                    const sel = document.getElementById(`add-member-${org.id}`) as HTMLSelectElement
                                    if (sel?.value) { handleAddMember(org.id, sel.value); sel.value = '' }
                                  }}
                                  className="font-body text-caption font-medium text-action hover:text-action/80 transition-colors"
                                >
                                  + Add
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
          {/* Resize handle */}
          <div onMouseDown={onResizeStart} className="h-2 cursor-ns-resize flex items-center justify-center hover:bg-border/30 transition-colors rounded-b-lg" title="Drag to resize">
            <div className="w-8 h-0.5 bg-border/40 rounded-full" />
          </div>
        </>
      )}
    </section>
  )
}

/* ── Inline Create Form ──────────────────────────────── */

function CreateOrgInline({ onSubmit, onCancel }: {
  onSubmit: (name: string, description: string) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')

  return (
    <div className="bg-base rounded-lg border border-action/30 p-3 space-y-2">
      <div className="flex items-center gap-3">
        <input
          className="flex-1 bg-card border border-border rounded px-2 py-1 font-body text-caption text-text-primary"
          value={name} onChange={e => setName(e.target.value)} placeholder="Organization name"
          autoFocus
        />
        <input
          className="flex-1 bg-card border border-border rounded px-2 py-1 font-body text-caption text-text-primary"
          value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => { if (name.trim()) onSubmit(name.trim(), desc.trim()) }}
          disabled={!name.trim()}
          className="font-body text-caption font-medium bg-action text-white px-3 py-1 rounded hover:bg-action/80 disabled:opacity-40 transition-colors"
        >
          Create
        </button>
        <button
          onClick={onCancel}
          className="font-body text-caption text-text-secondary hover:text-text-primary transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
